import { toQueryString } from '@croquiscom-pvt/web2app';

import { isBrowser } from '@common/device-manager';

import { checkZPayFunctionExists, ZpayEvent } from '../ZpayEvent';
import { ZPayFnOptions } from './types';

import { UrlObject } from 'url';

/**
 * UrlObject를 문자열로 변환하는 공통 함수
 */
export const urlToString = (url: string | UrlObject): string => {
  if (typeof url === 'string') {
    return url;
  }

  const pathname = url.pathname || '';
  const query = url.query;

  if (query) {
    const queryString = typeof query === 'string' ? query : toQueryString(query);
    return `${pathname}?${queryString}`;
  }

  return pathname;
};

export const fetchCheckoutToUrl = <T extends string | UrlObject>(url: T): T => {
  if (isBrowser() && window.location.pathname.startsWith('/checkout/')) {
    if (typeof url === 'object' && url.pathname) {
      url.pathname = `/checkout${url.pathname.replace(/^\/checkout/, '')}`;
    } else if (typeof url === 'string' && !url.startsWith('http')) {
      url = `/checkout${url.replace(/^\/checkout\//, '/')}` as T;
    }
  }
  return url;
};

export const handleRouteForZPay =
  (url: string | UrlObject, zpayFnOptions: ZPayFnOptions) =>
  (fallback: () => void = () => undefined) => {
    const parsed_url = urlToString(url);

    const title = zpayFnOptions.title ?? '';
    const type = zpayFnOptions.type ?? 'normal';
    const href = isAbsoluteUrl(parsed_url) ? parsed_url : window.location.origin + parsed_url;

    if (zpayFnOptions.disable_zpay_function || !checkZPayFunctionExists(window)) {
      return fallback();
    }
    switch (zpayFnOptions.action) {
      case 'close':
        return ZpayEvent.close();
      case 'createOrderSheet':
        return ZpayEvent.createOrderSheet(href, title, type);
      default:
        return ZpayEvent.open(href, title, type);
    }
  };

export const getQuerySeparator = (url: string) => {
  try {
    return new URL(url).search.length > 0 ? '&' : '?';
  } catch (e) {
    return '';
  }
};

export const buildUrl = (base: string, separator: string, ...queries: (string | undefined)[]): string => {
  const filtered_queries = queries.filter(Boolean).join('&');
  return filtered_queries ? `${base}${separator}${filtered_queries}` : base;
};

/**
 * 서버사이드에서 path에 /checkout이 없으면 추가하는 함수
 * @param path 원본 path 문자열
 * @returns /checkout이 추가된 혹은 원본 path 문자열
 */
export const addCheckoutToServerPath = (path: string): string => {
  // 이미 http나 https로 시작하는 외부 URL인 경우 그대로 반환
  if (path.startsWith('http')) {
    return path;
  }

  // 이미 /checkout으로 시작하는 경우 그대로 반환
  if (path.startsWith('/checkout/')) {
    return path;
  }

  // path가 /로 시작하지 않는 경우 /를 추가
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // /checkout 추가
  return `/checkout${normalizedPath}`;
};

// 상수 정의
export const HTTP_URL_REGEX = /^https?:\/\//;

/**
 * URL이 외부 HTTP/HTTPS URL인지 확인합니다.
 * @param url - 확인할 URL 문자열
 * @returns 외부 URL이면 true, 내부 URL이면 false
 */
export const isAbsoluteUrl = (url: string): boolean => {
  return HTTP_URL_REGEX.test(url);
};

/**
 * URL이 지그재그, 포스티, 패션바이카카오 외부 URL인지 확인합니다.
 * @param url - 확인할 URL 문자열 (절대 URL)
 * @returns 외부 URL이면 true, 내부 URL이면 false
 */
export const isExternalUrl = (url: string): boolean => {
  if (!isAbsoluteUrl(url)) return false;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    // 내부 도메인 (루트 도메인 단위로 작성)
    const internal_domains = ['zigzag.kr', 'posty.kr', 'fashionbykakao.com'];

    for (const domain of internal_domains) {
      if (host === domain || host.endsWith(`.${domain}`)) {
        return false;
      }
    }

    return true; // 모든 내부 도메인과 일치하지 않으면 외부
  } catch (e) {
    // 파싱 실패 시 안전하게 내부로 간주
    return false;
  }
};
